/**
 * A drop's gateway cell, drawn like its picker (user, 20/10/2026: "show the
 * actual gateway like this, we can see exactly what it can be"): "auto" or
 * the pin, then the gateway(s) it can go through — each letter in its
 * colour — the name, the route number when it is one gateway, and what it
 * sends with (its sender ID, or the AI badge). An automatic drop not in
 * today's plan shows every gateway its strategy may pick.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Pin, RadioTower } from 'lucide-react';

import { SendsWithCell } from '../../../../../gateways/senders/SendsWithCell';
import type { SlotGatewayLabel } from '../../slot-gateway-labels';
import { AutoTag } from '../AutoTag';
import { GatewayLetter } from '../GatewayLetter';

const SHOWN_LETTERS = 4;

const Letters: FC<{ indexes: number[] }> = ({ indexes }) => {
  if (indexes.length === 0) return <RadioTower className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />;
  const rest = indexes.length - SHOWN_LETTERS;
  return (
    <span className="flex shrink-0 items-center gap-0.5">
      {indexes.slice(0, SHOWN_LETTERS).map((i) => <GatewayLetter key={i} index={i} />)}
      {rest > 0 ? <span className="text-[10px] text-muted-foreground">+{rest}</span> : null}
    </span>
  );
};

export const GatewayCellLabel: FC<{ label: SlotGatewayLabel; autoTitle: string }> = ({ label, autoTitle }) => (
  <>
    {label.automatic ? <AutoTag title={autoTitle} /> : <Pin className="h-3.5 w-3.5 shrink-0 text-primary" aria-label="Fixed on this drop" />}
    <Letters indexes={label.indexes} />
    <span className="min-w-0 truncate text-xs font-medium">{label.title}</span>
    {label.sends ? (
      <>
        {label.routeId !== null ? <span className="shrink-0 font-mono text-[10.5px] tabular-nums text-muted-foreground">#{label.routeId}</span> : null}
        <span className="ml-auto shrink-0">
          <SendsWithCell value={label.sends} max={1} nowrap />
        </span>
      </>
    ) : (
      <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">· {label.detail}</span>
    )}
  </>
);
